import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Typo from 'constants/typography'
import Icon from '../icon'
import ICONS, { ICON_SIZE } from '../icon/const'
import InputLayout from '../input-layout'
import styles from './style.scss'
import { SELECT_TYPE_CLASS, SELECT_TYPE } from './const'

/**
 * @description this component can apply any `<select />`'s props
 * @param {string} props.label label of dropdown
 * @param {string} props.helperText the message that show at the bottom of dropdown
 * @param {boolean} props.error flag to show with error appearance
 * @param {boolean} props.fullWidth flag to show select with fullWidth
 * @param {func} props.onChange event callback that will be trigger when the value of select was changed. `(changedValue, event) => void`
 * @param {element} props.children should be `<option />`
 * @param {string} props.type can be set from SELECT_TYPE eg. `standard` `float`
 * @param {string} props.rootClassName styles applied to the select component root (layout) classes
 * @param {string} props.className customize style of component
 * @param {function | Object} props.inputRef input element's ref (can be function or object)
 * @param {boolean} props.disable flag to show with disable appearance
 * @param {function} props.onclick event callback when clicked element
 * @param {boolean} props.preventExpand flag to prevent expand in select
 */
const Select = ({
  cy,
  label,
  helperText,
  error,
  children,
  fullWidth,
  inputRef,
  onChange,
  type,
  className,
  rootClassName,
  disabled,
  onClick,
  preventExpand,
  selectClassName,
  ...props
}) => {
  const selectType = SELECT_TYPE_CLASS[type] || SELECT_TYPE_CLASS.standard
  const classes = clsx(
    Typo.body1,
    'pml-dropdown__container',
    selectType,
    className,
    {
      error,
      disabled,
      'prevent-expand': preventExpand,
    },
  )
  const selectClasses = clsx('pml-dropdown__select', selectClassName, {
    'full-width': fullWidth,
  })

  const handleChanged = useCallback(
    (event) => {
      if (onChange) {
        onChange(event, event.target.value)
      }
    },
    [onChange],
  )

  const handleRef = useCallback(
    (ref) => {
      if (typeof inputRef === 'function') {
        inputRef(ref)
        // eslint-disable-next-line no-prototype-builtins
      } else if (inputRef?.hasOwnProperty('current')) {
        // eslint-disable-next-line no-param-reassign
        inputRef.current = ref
      }
    },
    [inputRef],
  )

  return (
    <InputLayout
      label={label}
      fullWidth={fullWidth}
      shrink
      helperText={helperText}
      error={error}
      className={rootClassName}
    >
      <style jsx>{styles}</style>
      <div
        data-cy="pml-dropdown-input-container"
        className={classes}
        onClick={onClick}
        onKeyDown={onClick}
        role="button"
        tabIndex={0}
      >
        <select
          data-cy={cy || 'pml-dropdown-input'}
          className={selectClasses}
          onChange={handleChanged}
          ref={handleRef}
          {...props}
        >
          {children}
        </select>
        <div className="pml-dropdown__icon">
          <Icon
            size={ICON_SIZE.small}
            src={ICONS.dropdownSmall}
            disabled={disabled}
          />
        </div>
      </div>
    </InputLayout>
  )
}

Select.defaultProps = {
  cy: undefined,
  label: undefined,
  helperText: undefined,
  error: false,
  fullWidth: false,
  type: undefined,
  className: undefined,
  rootClassName: undefined,
  inputRef: undefined,
  onChange: undefined,
  onClick: undefined,
  disabled: undefined,
  preventExpand: undefined,
  selectClassName: undefined,
}

Select.propTypes = {
  cy: PropTypes.string,
  label: PropTypes.string,
  helperText: PropTypes.string,
  children: PropTypes.node.isRequired,
  error: PropTypes.bool,
  fullWidth: PropTypes.bool,
  type: PropTypes.oneOf(Object.values(SELECT_TYPE)),
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  inputRef: PropTypes.oneOfType([
    PropTypes.shape({
      current: PropTypes.shape({}),
    }),
    PropTypes.func,
  ]),
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  preventExpand: PropTypes.bool,
  selectClassName: PropTypes.string,
}

export default Select
